import { IonSpinner } from '@ionic/react';
import classNames from 'classnames';
import { CommunityQuestion, FullCommunityQuestion, FullPost } from 'model';
import React from 'react';
import { useAddPostImageMutation } from '../api/PostApi';
import { FormHookReturnType, FormValuesType } from '../hooks/useFormValues';
import { useImageUpload } from '../hooks/useImageUpload';
import { usePasteURL } from '../hooks/usePasteURL';
import { BigImageContainer } from './BigImageContainer';
import { FormInput } from './Forms/FormInput';
import { FormTextarea } from './Forms/FormTextarea';
import { TopicSearchBox } from './Forms/TopicSearchBox';
import { LinkPreviewComponent } from './LinkPreviewComponent';
import { UploadImageButton } from './UploadImageButton';

// styles
// eslint-disable-next-line css-modules/no-unused-class
import styles from './PostEditFields.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from './Forms/FormFields.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../styles/posts.module.css';
import { filterInlineImages } from '../util/filterInlineImages';
import { FormInlineImageArea } from './Forms/FormInlineImageArea';

export const CommunityQuestionEditFields: React.FC<{
	post: FullCommunityQuestion;
	setValues: FormHookReturnType<CommunityQuestion>['setValues'];
	formValues: FormValuesType<CommunityQuestion>;
	setPost: (post: FullPost | undefined | null) => void;
}> = ({ post, setValues, formValues, setPost }) => {
	const { uploadImage } = useImageUpload(post, useAddPostImageMutation);
	const { onPaste: onPasteSocial, loading: loadingSocial } = usePasteURL<CommunityQuestion>(setValues, 'contents');
	const onPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
		onPasteSocial(e);
		uploadImage(e);
	};

	return (
		<>
			<div className={formStyles.fieldsContainer}>
				{formValues && (
					<>
						<FormInput
							id="title"
							setValues={setValues}
							field={formValues.title}
							label="Question"
							testId="editPostTitleField"
						/>
						<TopicSearchBox
							label="Topic"
							topic={formValues.topic?.value ?? ''}
							setValues={setValues}
							errors={formValues.topic?.errors}
							testId="editPostTopicField"
						/>
						<div className={loadingSocial ? styles.fieldFade : ''}>
							<FormInlineImageArea
								setValues={setValues}
								content={formValues?.contents?.value}
								document={post}
							>
								<FormTextarea
									id="contents"
									nogap={post.images.length < 1}
									rows={7}
									onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
										if (loadingSocial) e.preventDefault();
									}}
									setValues={setValues}
									field={formValues.contents}
									label="Elaborate"
									placeholder="Type your question, paste images or links to embed them"
									onPaste={e => onPaste(e)}
									testId="editPostContentField"
								/>
								<div className={classNames(styles.loading, loadingSocial ? styles.show : '')}>
									<div>Fetching Preview</div>
									<IonSpinner style={{ height: '15px' }} />
								</div>
							</FormInlineImageArea>
						</div>
						{post.images.length > 0 && (
							<div className={postStyles.postImageContainer}>
								<BigImageContainer
									post={post}
									edit={true}
									images={filterInlineImages(formValues?.contents?.value as string, post.images)}
								/>
							</div>
						)}
					</>
				)}
				<LinkPreviewComponent setValues={setValues} linkPreview={formValues.linkPreview?.value} />
				<div className={styles.postFooter}>
					<div />
					{post.images.length < 6 && <UploadImageButton post={post} />}
				</div>
			</div>
		</>
	);
};
